// Phase 2.2 (hh-e5n) — ItemCard. Includes the orphan-badge / "положить
// куда-то" return-to-place flow from Phase 2.8 (hh-qq9, case-06).
//
// Ported from designproject/files/screens-card.jsx (`ItemCard`). The design
// read static `window.ITEMS` / `window.CONTAINERS`; here the item is loaded
// from the live property tree (see useItem.ts — there is no single-item
// endpoint), move history from GET /api/v1/items/{id}/history, and the move
// flow opens MoveItemSheet (POST /api/v1/items/{id}/move).
//
// Delete flow: the full two-phase DestructPreview (POST /destruct/preview →
// /destruct/confirm) is available on the backend, but its typed API client
// (api/endpoints/destruct.ts) is still a stub. Per the task brief, for a
// single item v1 uses the simpler DELETE /api/v1/items/{id} guarded by an
// inline confirm strip — no DestructPreviewScreen wiring. Cascade preview for
// containers can be added when the destruct client lands (issue hh-78y).

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import {
  T,
  Icon,
  Chip,
  Btn,
  Avatar,
  SectionLabel,
  entityIcon,
  type IconName,
} from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { items, humanizeError } from '../../api';
import type { ItemHistoryEntry } from '../../api/endpoints/items.js';
import { useItem } from './useItem';
import { MoveItemSheet } from './MoveItemSheet';

export interface ItemCardProps {
  readonly router: RouterApi;
  readonly itemId: string;
}

type HistoryStatus = 'loading' | 'ready' | 'error';

function describeError(err: unknown): string {
  return humanizeError(err, 'delete');
}

function actorLabel(kind: string): string {
  // Backend (ItemHistory.ActorKind) emits "ai" for assistant-initiated moves
  // and "user"/"manual" for человек. Map both wire spellings explicitly so an
  // AI move is not mislabelled as a manual one (aidepo-41 actor swap).
  switch (kind) {
    case 'ai':
    case 'agent':
      return 'ассистент';
    case 'system':
      return 'системой';
    default:
      return 'вы';
  }
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * "из «Комната» (Собственность) → в «Комната» (Собственность)" label for one
 * history row, falling back gracefully. Property name is shown in parentheses
 * alongside the room so identically-named rooms in different собственности
 * («Кладовка» в Даче vs в Квартире) are distinguishable (aidepo-41). When only
 * a property is known (room-less move) it stands alone.
 */
function placeLabel(roomName: string | null, propertyName: string | null): string | null {
  if (roomName !== null) {
    return propertyName !== null ? `«${roomName}» (${propertyName})` : `«${roomName}»`;
  }
  if (propertyName !== null) return `«${propertyName}»`;
  return null;
}

function moveLabel(h: ItemHistoryEntry): string {
  const from = placeLabel(h.fromRoomName, h.fromPropertyName);
  const to = placeLabel(h.toRoomName, h.toPropertyName);
  if (from !== null && to !== null) return `из ${from} → в ${to}`;
  if (to !== null) return `помещено в ${to}`;
  if (from !== null) return `убрано из ${from}`;
  return 'перемещение';
}

// ─────────────────────────────────────────────────────────
// History timeline
// ─────────────────────────────────────────────────────────

function useItemHistory(itemId: string): {
  readonly status: HistoryStatus;
  readonly rows: readonly ItemHistoryEntry[];
  reload(): void;
} {
  const [status, setStatus] = useState<HistoryStatus>('loading');
  const [rows, setRows] = useState<readonly ItemHistoryEntry[]>([]);
  // nonce bumps on reload() so the move history refetches after a move —
  // without it the card stayed «Не перемещалась» post-move (hh-cvy.2).
  const [nonce, setNonce] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    items
      .getItemHistory(itemId)
      .then((res) => {
        if (cancelled) return;
        setRows(res.history);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, nonce]);

  return { status, rows, reload: () => setNonce((n) => n + 1) };
}

function HistoryRow({
  entry,
  current,
}: {
  readonly entry: ItemHistoryEntry;
  readonly current: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px',
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: 5,
          marginTop: 5,
          background: current ? T.brand : T.borderStrong,
          flexShrink: 0,
          boxShadow: current ? `0 0 0 4px ${T.brandSoft}` : 'none',
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: current ? 700 : 600,
            color: T.ink,
          }}
        >
          {moveLabel(entry)}
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>
          {formatTimestamp(entry.changedAt)} · {actorLabel(entry.actorKind)}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Action tile
// ─────────────────────────────────────────────────────────

function ActionTile({
  icon,
  label,
  onClick,
  tone,
  disabled,
}: {
  readonly icon: IconName;
  readonly label: string;
  readonly onClick: () => void;
  readonly tone?: 'danger';
  readonly disabled?: boolean;
}): JSX.Element {
  const color = tone === 'danger' ? T.danger : T.ink;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '12px 6px',
        background: T.bg,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        fontFamily: T.font,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        color,
      }}
    >
      <Icon name={icon} size={18} color={color} />
      <div style={{ fontSize: 12, fontWeight: 650 }}>{label}</div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────
// State screens
// ─────────────────────────────────────────────────────────

function CenteredNote({
  title,
  detail,
  onBack,
  onRetry,
}: {
  readonly title: string;
  readonly detail?: string;
  readonly onBack: () => void;
  readonly onRetry?: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.bg,
      }}
    >
      <ScreenHeader title="Карточка вещи" onBack={onBack} />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{title}</div>
        {detail !== undefined && (
          <div style={{ fontSize: 13, color: T.ink3 }}>{detail}</div>
        )}
        {onRetry !== undefined && (
          <Btn kind="outline" onClick={onRetry} style={{ marginTop: 6 }}>
            Повторить
          </Btn>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// ItemCard
// ─────────────────────────────────────────────────────────

export function ItemCard({ router, itemId }: ItemCardProps): JSX.Element {
  const state = useItem(itemId);
  const history = useItemHistory(itemId);
  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const back = (): void => router.back();

  if (state.status === 'loading') {
    return <CenteredNote title="Загружаю…" onBack={back} />;
  }
  if (state.status === 'not-found') {
    return (
      <CenteredNote
        title="Вещь не найдена"
        detail="Возможно, её уже удалили или перенесли."
        onBack={back}
      />
    );
  }
  if (state.status === 'error' || state.item === null) {
    return (
      <CenteredNote
        title="Не удалось загрузить"
        {...(state.error !== null ? { detail: state.error } : {})}
        onBack={back}
        onRetry={state.reload}
      />
    );
  }

  const { node, locationLabel, isContainer } = state.item;
  const isOrphan = node.isOrphan;

  const doDelete = (): void => {
    setDeleting(true);
    setActionError(null);
    items
      .deleteItem(itemId)
      .then(() => {
        router.back();
      })
      .catch((err: unknown) => {
        setActionError(describeError(err));
        setDeleting(false);
        setConfirmDelete(false);
      });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.bg,
      }}
    >
      <ScreenHeader
        title="Карточка вещи"
        onBack={back}
        trailing={
          <button
            type="button"
            onClick={() => router.go('edit_item', { id: itemId })}
            aria-label="Править"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              background: T.surface,
              border: `1px solid ${T.border}`,
              color: T.ink,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 2,
              padding: 0,
            }}
          >
            <Icon name="edit" size={18} />
          </button>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {/* Item header */}
        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.r3,
            padding: '20px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* hh-345: иконка вещи — ручная либо авто-подбор по названию. */}
            <Avatar
              kind="sys"
              icon={entityIcon('item', { icon: node.icon, name: node.name })}
              size={44}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 700,
                  letterSpacing: -0.4,
                  color: T.ink,
                }}
              >
                {node.name}
              </div>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 3 }}>
                {node.quantity > 1 ? `${node.quantity} шт.` : '1 шт.'}
                {isContainer && ` · контейнер (${state.item.containedCount})`}
              </div>
            </div>
            {isOrphan && (
              <Chip tone="safety" icon="pkg">
                без места
              </Chip>
            )}
          </div>

          {/* Current location / orphan call-to-action (case-06) */}
          {isOrphan ? (
            <div
              style={{
                background: T.safetySoft,
                border: '1px solid #E8D7A8',
                borderRadius: 12,
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon name="pkg" size={18} color={T.safety} />
                <div
                  style={{
                    flex: 1,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: T.safety,
                  }}
                >
                  Без места — нужно «приземлить»
                </div>
              </div>
              <Btn
                kind="primary"
                icon="move"
                onClick={() => setSheetOpen(true)}
                style={{ width: '100%' }}
              >
                Положить куда-то
              </Btn>
            </div>
          ) : (
            <div
              style={{
                background: T.bg,
                borderRadius: 12,
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Icon name="pin" size={18} color={T.brand} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}
                >
                  {locationLabel || '—'}
                </div>
                {/* case-40: деталь места ниже комнаты, если указана. */}
                {node.locationNote != null && node.locationNote !== '' ? (
                  <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 2 }}>
                    {node.locationNote}
                  </div>
                ) : null}
                <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2 }}>
                  текущее место
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            <ActionTile
              icon="pkg"
              label={isOrphan ? 'Положить' : 'Переместить'}
              onClick={() => setSheetOpen(true)}
            />
            <ActionTile
              icon="trash"
              label="Удалить"
              tone="danger"
              onClick={() => setConfirmDelete(true)}
            />
          </div>
        </div>

        {/* Delete confirm strip */}
        {confirmDelete && (
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background: T.dangerSoft,
              border: '1px solid #E9C7BF',
              borderRadius: T.r3,
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.danger }}>
              Удалить «{node.name}»?
            </div>
            <div style={{ fontSize: 12, color: T.ink2, marginTop: 2 }}>
              {isContainer
                ? 'Содержимое контейнера останется без места.'
                : 'Действие можно будет откатить в течение суток.'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Btn
                kind="ghost"
                size="sm"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                style={{ flex: 1 }}
              >
                Отмена
              </Btn>
              <Btn
                kind="danger"
                size="sm"
                icon="trash"
                onClick={doDelete}
                disabled={deleting}
                style={{ flex: 1.4 }}
              >
                {deleting ? 'Удаляю…' : 'Да, удалить'}
              </Btn>
            </div>
          </div>
        )}

        {actionError !== null && (
          <div
            style={{
              marginTop: 12,
              fontSize: 12.5,
              color: T.danger,
              fontWeight: 600,
            }}
          >
            {actionError}
          </div>
        )}

        {/* History */}
        <SectionLabel style={{ padding: '18px 4px 6px' }}>
          История перемещений
        </SectionLabel>
        {history.status === 'loading' && (
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r3,
              padding: 14,
              fontSize: 13,
              color: T.ink3,
            }}
          >
            Загружаю историю…
          </div>
        )}
        {history.status === 'error' && (
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r3,
              padding: 14,
              fontSize: 13,
              color: T.ink3,
            }}
          >
            Не удалось загрузить историю перемещений.
          </div>
        )}
        {history.status === 'ready' && history.rows.length === 0 && (
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r3,
              padding: 14,
              fontSize: 13,
              color: T.ink3,
            }}
          >
            Лежит на изначальном месте с момента добавления. Не перемещалась.
          </div>
        )}
        {history.status === 'ready' && history.rows.length > 0 && (
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r3,
              overflow: 'hidden',
            }}
          >
            {history.rows.map((entry, i) => (
              <div key={entry.id}>
                {i > 0 && (
                  <div style={{ height: 1, background: T.divider }} />
                )}
                <HistoryRow entry={entry} current={i === 0} />
              </div>
            ))}
          </div>
        )}
      </div>

      {sheetOpen && (
        <MoveItemSheet
          itemId={itemId}
          isContainer={isContainer}
          onClose={() => setSheetOpen(false)}
          onMoved={() => {
            setSheetOpen(false);
            state.reload();
            history.reload();
          }}
        />
      )}
    </div>
  );
}
